import '@atlaskit/css-reset';
import React, { Component, memo, type ComponentType, type PropsWithChildren } from 'react';
import isEqual from 'lodash/isEqual';
import pickBy from 'lodash/pickBy';
import noop from 'lodash/noop';
import { parse as fromQueryString, stringify as toQueryString } from 'query-string';
import type { EntryPointProps } from 'react-relay';
import type { Readonly } from 'loader-utils';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { metrics } from '@atlassian/browser-metrics';
import { fg } from '@atlassian/jira-feature-gating';
import { isFilterId } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import type { IssueNavigatorViewId } from '@atlassian/jira-issue-navigator/src/common/types.tsx';
import { convertToView } from '@atlassian/jira-issue-navigator/src/common/utils/index.tsx';
import { isShallowEqual } from '@atlassian/jira-platform-shallow-equal/src/index.tsx';
import RedirectIfProjectArchived from '@atlassian/jira-redirect-archived-project/src/ui/index.tsx';
import {
	toIssueKey,
	toProjectKey,
	type IssueKey,
} from '@atlassian/jira-shared-types/src/general.tsx';
import { ProjectSubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/project-sub-product-updater/index.tsx';
import {
	getUrlPrefix,
	isFilterKey,
} from '@atlassian/jira-spa-apps-project-issue-navigator-utils/src/index.tsx';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import {
	Redirect,
	RouterActions,
	RouterSubscriber,
	type Location,
	type Match,
	type RouterActionPush as Push,
	type RouterActionReplace as Replace,
} from '@atlassian/react-resource-router';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { NinErrorBoundary } from '@atlassian/jira-issue-navigator/src/common/ui/error-boundary/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { NestedEntryPoints } from '@atlassian/jira-spa-apps-issue-navigator-common/src/index.tsx';
import {
	useRoutePushOrReplace,
	type PushOrReplace,
} from '@atlassian/jira-spa-apps-issue-navigator-common/src/useRoutePushOrReplace.tsx';
import { useSelectedIssueParamRedirect } from '@atlassian/jira-spa-apps-issue-navigator-common/src/useSelectedIssueParamRedirect.tsx';
import { useFilterChangeInteraction } from '@atlassian/jira-spa-apps-issue-navigator-common/src/useFilterChangeInteraction.tsx';
import {
	useQueryParams,
	usePathParams,
	useCurrentRoute,
} from '@atlassian/jira-spa-apps-issue-navigator-common/src/router-selectors.tsx';
import { ISSUEKEY_REGEX } from '@atlassian/jira-common-match-jira-url/src/index.tsx';
import App from './ui/index.tsx';

type ProjectIssueNavigatorSPAWrapperProps = {
	// Remove when cleaning up jira_nin_last_used_jql_tech_changes
	push?: Push;
	// Remove when cleaning up jira_nin_last_used_jql_tech_changes
	replace?: Replace;
	// Remove when cleaning up jira_nin_last_used_jql_tech_changes
	location?: Location;
	// Remove when cleaning up jira_nin_last_used_jql_tech_changes
	match?: Match | null | undefined;
	showNav3Header?: boolean;
	preloadedEntryPoints?: NestedEntryPoints;
	pushOrReplace: PushOrReplace;
	projectKey: string | null | undefined;
	selectedIssue: string | undefined;
};

type ProjectIssueNavigatorSPAWrapperState = {
	initialised: boolean;
	issueKey: string;
};

type MutableSearchParams = {
	projectKey: string;
	issueKey: IssueKey;
	filter?: string;
	jql?: string;
	viewActivity?: boolean;
	groupBy?: string;
	hideDone?: string;
};

type InteractionProperties = {
	filterId?: string;
	view?: string;
};

const BROWSER_METRICS_INTERACTION_APP_KEY = 'jira.project-issue-navigator';

const initInteractions = () => ({
	pagination: {
		metrics: metrics.interaction({
			key: `${BROWSER_METRICS_INTERACTION_APP_KEY}.pagination.start-end.diff`,
		}),
		properties: {},
	},
	refinement: {
		metrics: metrics.interaction({
			key: `${BROWSER_METRICS_INTERACTION_APP_KEY}.refinement.start-end.diff`,
		}),
		properties: {},
	},
	filter: {
		metrics: metrics.interaction({
			key: `${BROWSER_METRICS_INTERACTION_APP_KEY}.filter.start-end.diff`,
		}),
		properties: {},
	},
	'list-view': {
		metrics: metrics.interaction({
			key: `${BROWSER_METRICS_INTERACTION_APP_KEY}.list-view.start-end.diff`,
		}),
		properties: {},
	},
	'split-view': {
		metrics: metrics.interaction({
			key: `${BROWSER_METRICS_INTERACTION_APP_KEY}.split-view.start-end.diff`,
		}),
		properties: {},
	},
	'column-configuration': {
		metrics: metrics.interaction({
			key: `${BROWSER_METRICS_INTERACTION_APP_KEY}.column-configuration.start-end.diff`,
		}),
		properties: {},
	},
});

const MaybeNinErrorBoundary = componentWithFG(
	'improve_nin_slo_events',
	NinErrorBoundary,
	({ children }: PropsWithChildren<{}>) => <>{children}</>,
);

type Interactions = ReturnType<typeof initInteractions>;
type InteractionKey = keyof Interactions;

// eslint-disable-next-line jira/react/no-class-components
class ProjectIssueNavigatorSPAWrapper extends Component<
	ProjectIssueNavigatorSPAWrapperProps,
	ProjectIssueNavigatorSPAWrapperState
> {
	constructor(props: ProjectIssueNavigatorSPAWrapperProps) {
		super(props);

		const { location } = props;
		this.activeInteractions = [];
		this.interactions = initInteractions();

		if (fg('jira_nin_last_used_jql_tech_changes')) {
			this.state = {
				initialised: false,
				issueKey: this.props.selectedIssue ?? '',
			};
			return;
		}
		if (fg('add_nin_press_interactions')) {
			// Ensure filter change event does not fire on initial loads & transitions because it
			// will cause the page load interaction to be aborted
			const { filter } = this.getQueryStringParams();
			this.lastFilter = filter;
		}

		const query = fromQueryString(location?.search ?? '');
		const issueKey = query.selectedIssue ?? '';

		this.state = {
			initialised: false,
			issueKey,
		};
	}

	shouldComponentUpdate(
		nextProps: Readonly<ProjectIssueNavigatorSPAWrapperProps>,
		nextState: Readonly<ProjectIssueNavigatorSPAWrapperState>,
	): boolean {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			return !(isShallowEqual(this.props, nextProps) && isShallowEqual(this.state, nextState));
		}
		return true;
	}

	componentDidUpdate(prevProps: ProjectIssueNavigatorSPAWrapperProps) {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			const prevIssueKey = prevProps.selectedIssue ?? '';
			const issueKey = this.props.selectedIssue ?? '';

			// If issue key has changed from the router and does not match our internal key then update our state
			if (issueKey !== prevIssueKey && issueKey !== this.state.issueKey) {
				this.setState({
					issueKey,
				});
			}
			return;
		}

		const prevQuery = fromQueryString(prevProps.location?.search ?? '');
		const query = fromQueryString(this.props.location?.search ?? '');

		const prevIssueKey = prevQuery.selectedIssue ?? '';
		const issueKey = query.selectedIssue ?? '';

		// If issue key has changed from the router and does not match our internal key then update our state
		if (issueKey !== prevIssueKey && issueKey !== this.state.issueKey) {
			this.setState({
				issueKey,
			});
		}
	}

	activeInteractions: InteractionKey[];

	interactions: Interactions;

	/** @deprecated Behind jira_nin_last_used_jql_tech_changes. Remove on cleanup */
	view: IssueNavigatorViewId | undefined = undefined;

	/** @deprecated Behind jira_nin_last_used_jql_tech_changes. Remove on cleanup */
	lastFilter: string | undefined;

	/**
	 * Update the currently selected issue in the URL.
	 *
	 * @param issueKey Issue key to update in the URL
	 * @param isSelectedByUserInteraction True if this issue was selected by a user interaction, e.g. clicking an issue
	 * in the list. This will be false if an issue was automatically selected by the app, e.g. automatically selecting
	 * the first issue once the issue list has loaded.
	 * @param viewActivity True to indicate the issue app should scroll to the activity feed section after opening.
	 */
	onChangeIssue = (
		issueKey: IssueKey,
		isSelectedByUserInteraction: boolean,
		viewActivity?: boolean,
	) => {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			this.pushOrReplace(
				{
					query: {
						selectedIssue: issueKey === '' ? undefined : issueKey,
						viewActivity: viewActivity?.toString(),
					},
				},
				// Replace the history entry when an issue is auto selected by the app so we're not pushing redundant entries to
				// the history stack
				!isSelectedByUserInteraction,
			);
			return;
		}

		// Replace the history entry when an issue is auto selected by the app so we're not pushing redundant entries to
		// the history stack
		this.push(
			{
				issueKey,
				viewActivity,
			},
			!isSelectedByUserInteraction,
		);
	};

	/**
	 * Set the currently selected view and update the selected issue in the URL if provided.
	 *
	 * @param newView View to update
	 * @param issueKey Issue key to update in the URL
	 */
	onSetView = (newView: IssueNavigatorViewId, issueKey: IssueKey) => {
		this.startInteraction(convertToView(newView));
		fg('add_nin_press_interactions') && traceUFOPress(`issue-navigator.${convertToView(newView)}`);

		if (fg('jira_nin_last_used_jql_tech_changes')) {
			this.pushOrReplace(
				{
					query: {
						selectedIssue: issueKey === '' ? undefined : issueKey,
						...(newView === 'detail' ? { groupBy: undefined, hideDone: undefined } : {}),
					},
				},
				true,
			);
			return;
		}
		this.view = newView;

		if (issueKey !== undefined || newView === 'detail') {
			this.push(
				{
					...(issueKey !== undefined ? { issueKey } : {}),
					...(newView === 'detail' ? { groupBy: undefined, hideDone: undefined } : {}),
				},
				true,
			);
		}
	};

	onRefinement = () => {
		this.startInteraction('refinement');
		fg('add_nin_press_interactions') && traceUFOPress('issue-navigator.refinement');
	};

	/**
	 * Callback function called when internal changes are made to PIN's state which generates new JQL.
	 *
	 * @param jql string The new JQL string
	 * @param clearFilter boolean When true we clear the filter from url params
	 */
	onChangeJql = (jql: string, clearFilter = false) => {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			this.pushOrReplace({
				query: {
					// Clear issue key from URL whenever JQL changes
					selectedIssue: undefined,
					...(clearFilter ? { filter: undefined } : {}),
					jql,
				},
			});
			return;
		}

		this.push({
			...(clearFilter ? { filter: undefined } : {}),
			jql,
			// Clear issue key from URL whenever JQL changes
			issueKey: toIssueKey(''),
		});
	};

	/**
	 * Callback function called when internal changes are made to PINs state which generates new JQL and groupBy.
	 * This allows us to update both values in the URL at the same time, allowing the data to be passed back down the tree.
	 * This is introduced strictly for jira_list_nin_merge changeboarding, and can be removed on cleanup.
	 *
	 * @param {Object} params - The new JQL and groupBy strings
	 * @param {string} [params.jql] - The new JQL string
	 * @param {string} [params.groupBy] - The new groupBy string
	 */
	onChangeJqlAndGroupBy = (params: { jql?: string; groupBy?: string }) => {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			this.pushOrReplace({
				query: {
					...(params.jql !== undefined ? { jql: params.jql, selectedIssue: undefined } : {}),
					...(params.groupBy !== undefined ? { groupBy: params.groupBy } : {}),
				},
			});
			return;
		}
		this.push({
			...(params.jql !== undefined ? { jql: params.jql, issueKey: toIssueKey('') } : {}),
			...(params.groupBy !== undefined ? { groupBy: params.groupBy } : {}),
		});
	};

	onPageDataLoad = (selectedView: IssueNavigatorViewId) => {
		if (!fg('jira_nin_last_used_jql_tech_changes')) {
			if (selectedView !== undefined) {
				this.view = selectedView;
			}
			if (!this.state.initialised) {
				this.setState({
					initialised: true,
				});
			}
		}

		this.endInteractions();
	};

	onStartFilterChangeInteractionIfAny = (filter?: string) => {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			throw new Error(
				'Method `onStartFilterChangeInteractionIfAny` deprecated behind jira_nin_last_used_jql_tech_changes and to be removed on cleanup',
			);
		}

		if (this.lastFilter !== filter) {
			this.lastFilter = filter;

			if (filter != null && (isFilterId(filter) || isFilterKey(filter))) {
				this.startInteraction('filter', {
					filterId: filter,
				});
				fg('add_nin_press_interactions') && traceUFOPress('issue-navigator.filter');
			}
		}
	};

	onChangeFilter = (filter?: string) => {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			this.pushOrReplace(
				{
					query: { ...(filter ? { filter, jql: undefined } : { jql: undefined }) },
				},
				true,
			);
			return;
		}

		this.push({ ...(filter ? { filter, jql: undefined } : { jql: undefined }) }, true);
	};

	onChangeColumnConfiguration = () => {
		this.startInteraction('column-configuration');
		fg('add_nin_press_interactions') && traceUFOPress('issue-navigator.column-configuration');
	};

	/** Dispatch route history action with provided attributes and set selected issue into state (if modified). */
	pushOrReplace: PushOrReplace = (attributes, shouldReplace) => {
		this.props.pushOrReplace(attributes, shouldReplace);
		if (attributes.query && 'selectedIssue' in attributes.query) {
			// We explicitly set this into state instead of being purely prop driven as browser history actions are not
			// re-rendered synchronously. Setting into state makes our render order more reliable.
			this.setState({
				issueKey: toIssueKey(attributes.query.selectedIssue ?? ''),
			});
		}
	};

	push(newParams: Partial<MutableSearchParams>, shouldReplace = false) {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			throw new Error(
				'Method `push` deprecated behind jira_nin_last_used_jql_tech_changes and to be removed on cleanup',
			);
		}

		const { match, push, location, replace } = this.props;
		const historyAction = shouldReplace ? replace : push;

		const oldIssueKey = this.state.issueKey;

		const {
			projectKey = match?.params.projectKey ?? '',
			issueKey = oldIssueKey ?? '',
			...params
		} = newParams;

		const newProjectKey = newParams.projectKey;
		const oldProjectKey = match?.params.projectKey;

		const projectKeyChanged =
			typeof newProjectKey !== 'undefined' && newProjectKey !== oldProjectKey;

		const newIssueKey = newParams.issueKey;

		const issueKeyParamsAreDefined = !!(newIssueKey || oldIssueKey);

		const issueKeyChanged = issueKeyParamsAreDefined && newIssueKey !== oldIssueKey;

		// @ts-expect-error - The "comma" option requires query-string v6.4.0 or newer. JFE currently uses v5.1.1.
		const query = fromQueryString(location?.search ?? '', { arrayFormat: 'comma' });

		const queryParamsChanged =
			Object.keys(params).length !== 0 &&
			!isEqual(
				pickBy(params, (param) => param != null),
				query,
			);

		if (projectKeyChanged || issueKeyChanged || queryParamsChanged) {
			const queryParams = {
				...query,
				...params,
				selectedIssue: newIssueKey,
			};

			if (newIssueKey == null || newIssueKey === '') {
				delete queryParams.selectedIssue;
			}

			const navigationItemId = match?.params.navigationItemId;
			const navigationItemIdSegment =
				navigationItemId && fg('saved_list_view_technical_changes') ? `${navigationItemId}/` : '';

			// @ts-expect-error - React-resource-router's external type interface claims push/replace only accept "string", however the underlying code works with the `Location` object we're using
			historyAction({
				pathname: `${(match && getUrlPrefix(match.url)) ?? ''}/projects/${projectKey}/${fg('jira_list_nin_merge') ? 'list' : 'issues'}/${navigationItemIdSegment}`,
				search: toQueryString(
					queryParams,
					// @ts-expect-error - The "comma" option requires query-string v6.4.0 or newer. JFE currently uses v5.1.1.
					{ arrayFormat: 'comma' },
				),
			});
		}

		if (issueKeyChanged) {
			// We explicitly set this into state instead of being purely prop driven as browser history actions are not
			// re-rendered synchronously. Setting into state makes our render order more reliable.
			this.setState({
				issueKey: issueKey ?? '',
			});
		}
		return true;
	}

	startInteraction = (key: InteractionKey, properties?: InteractionProperties) => {
		if (!this.activeInteractions.includes(key)) {
			this.activeInteractions.push(key);
		}
		const interaction = this.interactions[key];

		if (interaction) {
			interaction.properties = properties || {};
			interaction.metrics.start();
		}
	};

	endInteractions = () => {
		this.activeInteractions.forEach((key) => {
			this.interactions[key]?.metrics.stop({
				customData: this.interactions[key]?.properties,
			});
		});
		this.activeInteractions = [];
	};

	getQueryStringParams = () => {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			throw new Error(
				'Method `getQueryStringParams` deprecated behind jira_nin_last_used_jql_tech_changes and to be removed on cleanup',
			);
		}

		const { location } = this.props;
		const { filter, jql } = fromQueryString(location?.search ?? '', {
			// @ts-expect-error - The "comma" option requires query-string v6.4.0 or newer. JFE currently uses v5.1.1.
			arrayFormat: 'comma',
		});

		return { filter, jql };
	};

	render() {
		let projectKey;
		let filter;
		let jql;
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			projectKey = this.props.projectKey;
		} else {
			const { match, location } = this.props;
			projectKey = match?.params?.projectKey;
			const params = fg('add_nin_press_interactions')
				? this.getQueryStringParams()
				: // @ts-expect-error - The "comma" option requires query-string v6.4.0 or newer. JFE currently uses v5.1.1.
					fromQueryString(location?.search ?? '', { arrayFormat: 'comma' });
			filter = params.filter;
			jql = params.jql;

			this.onStartFilterChangeInteractionIfAny(filter);
		}

		return (
			<>
				<MarkProductStart />
				<MaybeNinErrorBoundary id="unhandled" packageName="jiraSpaAppsProjectIssueNavigator">
					<ProjectSubProductUpdater />
					<RedirectIfProjectArchived projectKey={projectKey ?? null} />
					<App
						showNav3Header={this.props.showNav3Header}
						projectKey={toProjectKey(projectKey ?? '')}
						issueKey={toIssueKey(this.state.issueKey)}
						jql={jql}
						onChangeIssue={this.onChangeIssue}
						onChangeJqlAndGroupBy={
							fg('jira_list_nin_merge_technical_changes') ? this.onChangeJqlAndGroupBy : undefined
						}
						onPageDataLoad={this.onPageDataLoad}
						onChangeColumnConfiguration={this.onChangeColumnConfiguration}
						onChangeJql={this.onChangeJql}
						initialised={this.state.initialised}
						onRefinement={this.onRefinement}
						onSetView={this.onSetView}
						view={
							this.view && !fg('jira_nin_last_used_jql_tech_changes')
								? convertToView(this.view)
								: undefined
						}
						filter={filter}
						onChangeFilter={this.onChangeFilter}
						preloadedEntryPoints={this.props.preloadedEntryPoints}
					/>
				</MaybeNinErrorBoundary>
			</>
		);
	}
}

const ProjectIssueNavigatorSPAWithRouterSubscriber: ComponentType<
	EntryPointProps<{}, {}, {}, {}>
> = ({ entryPoints }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return (
		<UFOSegment name="project-issue-navigator">
			<RouterActions>
				{({ push, replace }) => (
					<RouterSubscriber>
						{({ location, match, route }) => {
							let path = match.url;
							const navigationItemId = match.params.navigationItemId ?? '';
							const issueKeyRegex = new RegExp(`^${ISSUEKEY_REGEX}$`);
							const isIssueKey = issueKeyRegex.test(navigationItemId);
							const queryParams = fromQueryString(location.search);

							/*
							 * At some point the route config was changed from:
							 *
							 * .../:issueKey
							 * to
							 * .../:navigationItemId
							 *
							 * Where the :issueKey path param represented the selected issue key
							 * and the :navigationItemId path param represents the navigation item ID.
							 *
							 * We are performing a frontend redirect when we detect that an issue key
							 * is present in the position where the navigation item ID is expected.
							 *
							 * This ensures we maintain backwards compatibility with existing links
							 * when performing a SPA transition.
							 */
							if (isIssueKey) {
								const pathSegments = path.split('/').filter(Boolean);

								const updatedPathSegments = pathSegments.filter(
									(segment) => segment !== navigationItemId,
								);
								path = `/${updatedPathSegments.join('/')}`;

								queryParams.selectedIssue = navigationItemId;
							}

							let url = path;

							if (Object.keys(queryParams).length) {
								url += `?${toQueryString(queryParams)}`;
							}

							if (isIssueKey) {
								fireUIAnalytics(createAnalyticsEvent({}), 'route redirected', {
									didIssueKeyRedirect: true,
								});

								return <Redirect to={url} />;
							}

							return (
								<ProjectIssueNavigatorSPAWrapper
									push={push}
									location={location}
									match={match}
									replace={replace}
									showNav3Header={
										fg('jira_list_nin_merge')
											? route.name === 'servicedesk-issues' || route.name === 'servicedesk-list'
											: route.name === 'servicedesk-issues'
									}
									preloadedEntryPoints={entryPoints}
									pushOrReplace={noop}
									projectKey={undefined}
									selectedIssue={undefined}
								/>
							);
						}}
					</RouterSubscriber>
				)}
			</RouterActions>
		</UFOSegment>
	);
};

const ProjectIssueNavigatorSPA: ComponentType<EntryPointProps<{}, {}, {}, {}>> = ({
	entryPoints,
}) => {
	const query = useQueryParams();
	const params = usePathParams();
	const route = useCurrentRoute();
	const pushOrReplace = useRoutePushOrReplace(query, params, route);

	// Handle redirecting from /:issueKey path param to ?selectedIssue query param
	useSelectedIssueParamRedirect(params, pushOrReplace);

	useFilterChangeInteraction(query);

	return (
		<UFOSegment name="project-issue-navigator">
			<ProjectIssueNavigatorSPAWrapper
				pushOrReplace={pushOrReplace}
				projectKey={params.projectKey}
				selectedIssue={query.selectedIssue}
				showNav3Header={
					fg('jira_list_nin_merge')
						? route.name === 'servicedesk-issues' || route.name === 'servicedesk-list'
						: route.name === 'servicedesk-issues'
				}
				preloadedEntryPoints={entryPoints}
			/>
		</UFOSegment>
	);
};

export default componentWithFG(
	'jira_nin_last_used_jql_tech_changes',
	memo(ProjectIssueNavigatorSPA),
	memo(ProjectIssueNavigatorSPAWithRouterSubscriber),
);
